import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    NgZone,
    OnDestroy,
    OnInit,
    computed,
    inject,
    viewChild,
} from '@angular/core';
import { ScrollDetail } from '@ionic/angular';
import { IonButtons, IonContent, IonFooter, IonHeader, IonMenuButton, IonTitle, IonToolbar } from '@ionic/angular/standalone';
import { sampleData } from './sample-data';
import { BrowserLogFileReader } from '../../hgm-setup/src/module/util/browser-log-file-reader';
import { LogFileProvider } from '../../hgm-setup/src/module/util/log-file-provider';
import { LOGGER } from '../../hgm-setup/src/module/util/logger';
import { DataProvider } from '../../providers/data-provider';
import { SignalGroupProvider } from '../../providers/signal-group-provider';
import { TabChartsPageComponent } from '../tab-charts/tab-charts.page';
import { ViewfinderChartComponent } from '../tab-charts/viewfinder-chart.component';

@Component({
    selector: 'app-tabs',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        IonHeader,
        IonToolbar,
        IonTitle,
        IonButtons,
        IonMenuButton,
        IonContent,
        IonFooter,
        TabChartsPageComponent,
        ViewfinderChartComponent,
    ],
    template: `
        <div class="ion-page" id="main">
            <ion-header #mainHeader class="ion-no-border" [translucent]="true" style="transition: transform 0.3s ease">
                <ion-toolbar>
                    <ion-buttons slot="start">
                        <ion-menu-button></ion-menu-button>
                    </ion-buttons>
                    <ion-title class="center-title" [color]="fileName ? 'default' : 'medium'">
                        <div style="pointer-events: auto">
                            {{ fileName ? fileName : 'HGM Charts' }}
                            @if (appliedConfigurationFileName) {
                                <span
                                    style="
                                        font-size: 0.5em;
                                        font-weight: normal;
                                        margin-left: 10px;
                                        color: var(--ion-color-step-600);
                                        display: block;
                                    "
                                >
                                    Applied chart configuration: {{ appliedConfigurationFileName }}
                                </span>
                            }
                        </div>
                    </ion-title>
                </ion-toolbar>
            </ion-header>

            <ion-content [fullscreen]="true" [scrollEvents]="true" (ionScroll)="onScroll($event)" scrollY="true" #scrollContent>
                <div style="display: flex; flex-direction: column; min-height: 100%; padding-top: 10px" #chartsContainer>
                    <app-tab-charts
                        #chartsTab
                        [tabHeight]="chartsTabHeight"
                        [viewfinderIsUpdating]="viewfinderIsUpdating"
                        style="display: block; padding-bottom: 20px"
                    ></app-tab-charts>
                </div>
            </ion-content>

            @if (showViewfinder()) {
                <ion-footer class="ion-no-border">
                    <ion-toolbar style="--min-height: 0; padding: 0">
                        <viewfinder-chart #viewfinder (isUpdating)="onViewfinderIsUpdating($event)"></viewfinder-chart>
                    </ion-toolbar>
                </ion-footer>
            }
        </div>
    `,
    styles: `
        .hidden {
            display: none !important;
        }

        .main-container {
            display: flex;
            flex-direction: column;
            height: 100%;
            overflow: hidden;
        }

        .charts-wrapper {
            flex-grow: 1;
            flex-basis: 0;
            min-height: 0;
            overflow-y: hidden;
        }

        viewfinder-chart {
            background-color: var(--ion-background-color);
            --background-color: var(--ion-background-color);
        }

        ion-title.center-title {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            text-align: center !important;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            pointer-events: none;
            padding: 0;
        }

        @media (max-width: 576px) {
            ion-title.center-title {
                font-size: 0.9rem;
            }
        }
    `,
})
export class TabsPageComponent implements OnInit, AfterViewInit, OnDestroy {
    isLoading = false;
    fileInputAccept: string;
    chartsTabHeight = 0;
    viewfinderIsUpdating = true;
    readonly showViewfinder = computed(() => !!this.dataProvider.data());

    // #scrollContent and #mainHeader live on Ionic components, so we need `read: ElementRef`
    // to get the host element instead of the component instance.
    private scrollContent = viewChild('scrollContent', { read: ElementRef });
    private mainHeader = viewChild('mainHeader', { read: ElementRef });

    private resizeObserver: ResizeObserver;
    private lastScrollTop = 0;

    private readonly dataProvider = inject(DataProvider);
    private readonly signalGroupProvider = inject(SignalGroupProvider);
    private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly logFileProvider = inject(LogFileProvider);
    private readonly logReader = inject(BrowserLogFileReader);
    private readonly zone = inject(NgZone);
    private readonly logger = inject(LOGGER);

    constructor() {
        this.resizeObserver = new ResizeObserver(this.onResize);
    }

    ngOnInit() {
        this.resizeObserver.observe(this.element.nativeElement);
        window.addEventListener('resize', this.onResize);
    }

    ngAfterViewInit() {
        const scrollContent = this.scrollContent();
        if (scrollContent) {
            this.resizeObserver.observe(scrollContent.nativeElement);
            // Trigger initial calculation
            this.onResize();
        }

        if (sampleData) {
            this.logReader.read({ data: sampleData, name: 'sample' }).then(
                (result) => this.logFileProvider.load(result),
                (err) => this.logger.error(err),
            );
        }
    }

    ngOnDestroy() {
        this.resizeObserver.disconnect();
        window.removeEventListener('resize', this.onResize);
    }

    onViewfinderIsUpdating(isUpdating: boolean) {
        this.viewfinderIsUpdating = isUpdating;
    }

    get fileName() {
        return this.logFileProvider.file && this.logFileProvider.parseResult && !this.logFileProvider.parseResult.err
            ? this.logFileProvider.file.file.name
            : undefined;
    }

    get appliedConfigurationFileName() {
        return this.signalGroupProvider.appliedConfigurationFileName();
    }

    onScroll(event: CustomEvent<ScrollDetail>) {
        const scrollTop = event.detail.scrollTop;
        const header = this.mainHeader();
        if (!header) return;

        if (scrollTop > this.lastScrollTop && scrollTop > 50) {
            header.nativeElement.style.transform = 'translateY(-100%)';
        } else if (scrollTop < this.lastScrollTop) {
            header.nativeElement.style.transform = 'translateY(0)';
        }

        this.lastScrollTop = Math.max(0, scrollTop);
    }

    private onResize = () =>
        this.zone.run(() => {
            const scrollContent = this.scrollContent();
            if (scrollContent) {
                // Determine available chart height from fixed viewport window
                this.chartsTabHeight = scrollContent.nativeElement.clientHeight;
            }
        });
}
